import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { bpsToPct, type DealAction } from '@accel/shared';
import { api } from '../lib/api';
import type { DealDetail } from '../lib/types';
import { DealDocuments } from '../components/DealDocuments';
import {
  Card,
  ErrorNote,
  Money,
  PartyCell,
  PipelineRail,
  Spinner,
  StatusTag,
  Tag,
  formatDate,
  formatDateTime,
} from '../components/ui';

/** How each action is labelled and how prominent it should look. */
const ACTION_META: Record<DealAction, { label: string; tone: 'primary' | 'ghost' | 'danger' }> = {
  SUBMIT: { label: 'Submit for financing', tone: 'primary' },
  VALIDATE: { label: 'Run validation & rating', tone: 'primary' },
  APPROVE_REVIEW: { label: 'Approve review', tone: 'primary' },
  REJECT: { label: 'Reject', tone: 'danger' },
  SEND_FOR_CONFIRMATION: { label: 'Send for confirmation', tone: 'primary' },
  CONFIRM: { label: 'Confirm in full', tone: 'primary' },
  CONFIRM_PARTIAL: { label: 'Confirm partial amount', tone: 'ghost' },
  DISPUTE: { label: 'Raise a query', tone: 'danger' },
  RESOLVE_DISPUTE: { label: 'Resolve dispute', tone: 'primary' },
  ROUTE: { label: 'Route to funder', tone: 'primary' },
  ACCEPT_OFFER: { label: 'Accept offer', tone: 'primary' },
  DECLINE_OFFER: { label: 'Decline offer', tone: 'ghost' },
  DISBURSE: { label: 'Disburse advance', tone: 'primary' },
  MARK_OUTSTANDING: { label: 'Mark outstanding', tone: 'ghost' },
  MARK_OVERDUE: { label: 'Mark overdue', tone: 'danger' },
  INVOKE_RECOURSE: { label: 'Invoke recourse', tone: 'danger' },
  SETTLE: { label: 'Record settlement', tone: 'primary' },
  WRITE_OFF: { label: 'Write off', tone: 'danger' },
  CANCEL: { label: 'Cancel', tone: 'ghost' },
};

/** Actions that move money must carry an idempotency key. */
const MONEY_ACTIONS: DealAction[] = ['DISBURSE', 'SETTLE', 'WRITE_OFF', 'CONFIRM_PARTIAL'];

export function DealDrawer({ dealId, onClose }: { dealId: string | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [error, setError] = useState<unknown>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [confirmUrl, setConfirmUrl] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['deal', dealId],
    queryFn: () => api.get<DealDetail>(`/deals/${dealId}`),
    enabled: Boolean(dealId),
  });

  // A link belongs to one deal; carrying it across would show the wrong one.
  const [shownFor, setShownFor] = useState<string | null>(null);
  if (shownFor !== dealId) {
    setShownFor(dealId);
    if (confirmUrl) setConfirmUrl(null);
  }

  const transition = useMutation({
    mutationFn: async ({ action, payload }: { action: DealAction; payload?: Record<string, unknown> }) => {
      // Sending for confirmation is more than a status change: it issues a
      // secure link and emails the payer. That lives behind its own endpoint
      // so the two can never drift apart.
      if (action === 'SEND_FOR_CONFIRMATION') {
        return api.post(`/deals/${dealId}/confirmation-request`, {});
      }

      // The key is derived from the deal and action, so a double-click or a
      // retried request cannot post the same journal twice.
      const idempotencyKey = MONEY_ACTIONS.includes(action)
        ? `${dealId}:${action}`
        : undefined;
      return api.post(
        `/deals/${dealId}/transitions`,
        { action, ...(payload ? { payload } : {}) },
        { idempotencyKey },
      );
    },
    onSuccess: (data, variables) => {
      setError(null);

      // Outside production the API returns the link, so an operator can walk
      // the payer through it without waiting for an email.
      const sent = data as { sentTo?: string; url?: string } | undefined;
      if (variables.action === 'SEND_FOR_CONFIRMATION' && sent?.sentTo) {
        setToast(`Confirmation link sent to ${sent.sentTo}`);
        if (sent.url) setConfirmUrl(sent.url);
      } else {
        setToast(ACTION_META[variables.action].label + ' — done');
      }
      setTimeout(() => setToast(null), 3200);
      void qc.invalidateQueries({ queryKey: ['deal', dealId] });
      void qc.invalidateQueries({ queryKey: ['deals'] });
      void qc.invalidateQueries({ queryKey: ['trial-balance'] });
      void qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
    onError: (err) => setError(err),
  });

  function run(action: DealAction) {
    setError(null);

    if (action === 'CONFIRM_PARTIAL') {
      const deal = query.data!;
      const entered = window.prompt(
        `Amount the payer accepts, in ${deal.currency} major units (face value is ${
          Number(deal.faceMinor) / 100
        }):`,
      );
      if (!entered) return;
      const minor = BigInt(Math.round(Number(entered) * 100));
      transition.mutate({ action, payload: { confirmedMinor: minor.toString() } });
      return;
    }

    transition.mutate({ action });
  }

  const deal = query.data;

  return (
    <>
      <div className={`scrim ${dealId ? 'show' : ''}`} onClick={onClose} />
      <aside className={`drawer ${dealId ? 'show' : ''}`}>
        {!dealId ? null : query.isLoading ? (
          <Spinner label="Loading deal" />
        ) : !deal ? (
          <ErrorNote error={query.error} />
        ) : (
          <>
            <div className="drawer-head">
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <div className="crumb">
                    {deal.type === 'ORDER' ? 'Order finance' : 'Invoice discounting'} ·{' '}
                    {deal.programme.name}
                  </div>
                  <h2 className="page-title" style={{ fontSize: 21, margin: '4px 0 6px' }}>
                    {deal.reference}
                  </h2>
                  <StatusTag status={deal.status} />
                </div>
                <button className="btn btn-ghost" onClick={onClose}>
                  Close
                </button>
              </div>

              <div style={{ marginTop: 18 }}>
                <PipelineRail status={deal.status} />
              </div>
            </div>

            <div className="drawer-body">
              <ErrorNote error={error} />

              {confirmUrl ? (
                <div
                  className="err"
                  style={{ background: 'var(--blue-soft)', color: 'var(--blue)', wordBreak: 'break-all' }}
                >
                  Confirmation link (shown in development only):{' '}
                  <a href={confirmUrl} target="_blank" rel="noreferrer">
                    {confirmUrl}
                  </a>
                </div>
              ) : null}

              {deal.availableActions.length > 0 ? (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 20 }}>
                  {deal.availableActions.map((action) => {
                    const meta = ACTION_META[action];
                    return (
                      <button
                        key={action}
                        className={`btn btn-${meta.tone}`}
                        disabled={transition.isPending}
                        onClick={() => run(action)}
                      >
                        {meta.label}
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="err" style={{ background: 'var(--slate-soft)', color: 'var(--slate)' }}>
                  This deal is closed — no further actions are available.
                </div>
              )}

              <div className="grid-2">
                <Card title="Supplier">
                  <PartyCell party={deal.sme} />
                </Card>
                <Card title="Payer">
                  <PartyCell party={deal.payer} />
                </Card>
              </div>

              <div className="section-title">Commercial terms</div>
              <Card>
                <dl className="dl">
                  <dt>Face value</dt>
                  <dd>
                    <Money minor={deal.faceMinor} currency={deal.currency} />
                  </dd>

                  <dt>Confirmed</dt>
                  <dd>
                    <Money minor={deal.confirmedMinor} currency={deal.currency} />
                    {deal.confirmedMinor && deal.confirmedMinor !== deal.faceMinor ? (
                      <span style={{ marginLeft: 8 }}>
                        <Tag tone="warning">diluted</Tag>
                      </span>
                    ) : null}
                  </dd>

                  <dt>Advance</dt>
                  <dd>
                    <Money minor={deal.advanceMinor} currency={deal.currency} />
                    {deal.advanceBps ? ` · ${bpsToPct(deal.advanceBps, 0)}` : ''}
                  </dd>

                  <dt>Discount fee</dt>
                  <dd>
                    <Money minor={deal.feeMinor} currency={deal.currency} />
                    {deal.feeBps ? ` · ${bpsToPct(deal.feeBps)}` : ''}
                  </dd>

                  <dt>Issued</dt>
                  <dd>{formatDate(deal.issueDate)}</dd>

                  <dt>Due</dt>
                  <dd>
                    {formatDate(deal.dueDate)} · {deal.terms}
                  </dd>

                  <dt>Region</dt>
                  <dd>
                    {deal.region}
                    {deal.eInvoiceCleared ? (
                      <span style={{ marginLeft: 8 }}>
                        <Tag tone="positive">e-invoice cleared</Tag>
                      </span>
                    ) : (
                      <span style={{ marginLeft: 8 }}>
                        <Tag tone="neutral">no clearance</Tag>
                      </span>
                    )}
                  </dd>
                </dl>
              </Card>

              {deal.assessments.length > 0 ? (
                <>
                  <div className="section-title">Credit assessment</div>
                  <Card
                    title={
                      <>
                        Score {deal.assessments[0]!.score} ·{' '}
                        <Tag
                          tone={
                            deal.assessments[0]!.decision === 'APPROVE'
                              ? 'positive'
                              : deal.assessments[0]!.decision === 'REFER'
                                ? 'warning'
                                : 'danger'
                          }
                        >
                          {deal.assessments[0]!.decision}
                        </Tag>
                        {deal.assessments[0]!.band ? ` · band ${deal.assessments[0]!.band}` : ''}
                      </>
                    }
                  >
                    {deal.assessments[0]!.factors?.factors?.length ? (
                      <>
                        <div className="ctx-label">Factors</div>
                        <table className="tbl" style={{ marginBottom: 16 }}>
                          <tbody>
                            {deal.assessments[0]!.factors.factors.map((f) => (
                              <tr key={f.code} style={{ cursor: 'default' }}>
                                <td>
                                  <span className="cell-t1">{f.label}</span>
                                  <span className="cell-t2" style={{ display: 'block' }}>
                                    {f.detail}
                                  </span>
                                </td>
                                <td className="r num">{f.score}</td>
                                <td className="r cell-t2">×{f.weight}</td>
                                <td className="r num">{f.contribution.toFixed(1)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </>
                    ) : null}

                    {deal.assessments[0]!.checks.length > 0 ? (
                      <div className="ctx-label">Checks</div>
                    ) : null}
                    {deal.assessments[0]!.checks.length === 0 &&
                    !deal.assessments[0]!.factors?.factors?.length ? (
                      <div className="cell-t2">
                        This assessment recorded a score without a stored breakdown. Re-running
                        validation produces the full factor and check detail.
                      </div>
                    ) : null}
                    {deal.assessments[0]!.checks.map((c) => (
                      <div key={c.code} className="timeline-item">
                        <span style={{ flexShrink: 0 }}>
                          <Tag tone={c.passed ? 'positive' : c.blocking ? 'danger' : 'warning'}>
                            {c.passed ? 'pass' : c.blocking ? 'blocked' : 'flag'}
                          </Tag>
                        </span>
                        <span>
                          <span className="cell-t1">{c.label}</span>
                          <span className="cell-t2" style={{ display: 'block' }}>
                            {c.detail}
                          </span>
                        </span>
                      </div>
                    ))}
                  </Card>
                </>
              ) : null}

              {deal.offers.length > 0 ? (
                <>
                  <div className="section-title">Funding offers</div>
                  <Card padded={false}>
                    <table className="tbl">
                      <thead>
                        <tr>
                          <th>Funder</th>
                          <th className="r">Advance</th>
                          <th className="r">Fee</th>
                          <th>Recourse</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {deal.offers.map((o) => (
                          <tr key={o.id} style={{ cursor: 'default' }}>
                            <td className="cell-t1">{o.lender.name}</td>
                            <td className="r">
                              <Money minor={o.advanceMinor} currency={deal.currency} />
                            </td>
                            <td className="r">
                              <Money minor={o.feeMinor} currency={deal.currency} />
                            </td>
                            <td>{o.recourse ? 'With recourse' : 'Non-recourse'}</td>
                            <td>
                              <Tag tone={o.status === 'ACCEPTED' ? 'positive' : 'neutral'}>
                                {o.status}
                              </Tag>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </Card>
                </>
              ) : null}

              {deal.journals.length > 0 ? (
                <>
                  <div className="section-title">Ledger</div>
                  {deal.journals.map((j) => (
                    <Card
                      key={j.id}
                      title={`${j.type.replace(/_/g, ' ')} · ${formatDateTime(j.postedAt)}`}
                      padded={false}
                    >
                      <table className="tbl">
                        <tbody>
                          {j.entries.map((e) => (
                            <tr key={e.id} style={{ cursor: 'default' }}>
                              <td style={{ width: 70 }}>
                                <Tag tone={e.side === 'DEBIT' ? 'info' : 'neutral'}>{e.side}</Tag>
                              </td>
                              <td>
                                <span className="cell-t1">
                                  {e.account.code} {e.account.name}
                                </span>
                                <span className="cell-t2" style={{ display: 'block' }}>
                                  {e.memo}
                                </span>
                              </td>
                              <td className="r">
                                <Money minor={e.amountMinor} currency={e.currency} />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </Card>
                  ))}
                </>
              ) : null}

              <div className="section-title">Timeline</div>
              <Card>
                {deal.events.map((e) => (
                  <div key={e.id} className="timeline-item">
                    <span className="timeline-seq">{e.sequence}</span>
                    <span style={{ flex: 1 }}>
                      <span className="cell-t1">
                        {e.fromStatus ? `${e.fromStatus} → ` : ''}
                        {e.toStatus}
                      </span>
                      <span className="cell-t2" style={{ display: 'block' }}>
                        {e.action} · {formatDateTime(e.occurredAt)}
                        {e.actorRole ? ` · ${e.actorRole}` : ''}
                        {e.reason ? ` · ${e.reason}` : ''}
                      </span>
                    </span>
                  </div>
                ))}
              </Card>

              <DealDocuments
                dealId={deal.id}
                dealType={deal.type}
                status={deal.status}
                documents={deal.documents}
              />
            </div>
          </>
        )}
      </aside>

      {toast ? (
        <div className="toast-wrap">
          <div className="toast">{toast}</div>
        </div>
      ) : null}
    </>
  );
}
